// Terms of Use & User Consent, transcribed from the canonical PDF:
// https://onecode-asset.s3.ap-south-1.amazonaws.com/Terms+of+Use+and+User+Consent+of+Customer+Experian.pdf
// Source PDF last modified 2025-10-15. Keep this file in sync if that document changes.
// Bold/italic runs and (a)-(s) list items mirror the source document.

export type TermsRun = {
  text: string
  bold?: boolean
  italic?: boolean
}

export type TermsBlock = {
  kind: 'paragraph' | 'listItem'
  number?: string
  marker?: string
  runs: TermsRun[]
}

export type TermsSection = {
  number: string
  title: string
  blocks: TermsBlock[]
}

export const TERMS_AND_CONDITIONS_DOC_URL =
  'https://onecode-asset.s3.ap-south-1.amazonaws.com/Terms+of+Use+and+User+Consent+of+Customer+Experian.pdf'

export const TERMS_AND_CONDITIONS: TermsSection[] = [
  {
    number: '1',
    title: 'INTRODUCTION',
    blocks: [
      {
        kind: 'paragraph',
        number: '1.1',
        runs: [
          {
            text: 'THIS DOCUMENT IS AN ELECTRONIC RECORD IN TERMS OF THE INFORMATION TECHNOLOGY ACT, 2000 AND RULES MADE THEREUNDER. THIS ELECTRONIC RECORD IS GENERATED BY A COMPUTER SYSTEM AND DOES NOT REQUIRE ANY PHYSICAL OR DIGITAL SIGNATURES.',
            bold: true,
          },
        ],
      },
      {
        kind: 'paragraph',
        number: '1.2',
        runs: [
          {
            text: 'THIS DOCUMENT IS PUBLISHED IN ACCORDANCE WITH THE PROVISIONS OF RULE 3 (1) OF THE INFORMATION TECHNOLOGY (INTERMEDIARY GUIDELINES AND DIGITAL MEDIA ETHICS CODE) RULES, 2021 THAT REQUIRES PUBLISHING THE RULES AND REGULATIONS, PRIVACY POLICY AND USER AGREEMENT FOR ACCESS OR USAGE OF THE APP.',
            bold: true,
          },
        ],
      },
      {
        kind: 'paragraph',
        number: '1.3',
        runs: [
          { text: 'The domain name ' },
          { text: 'partner.zetapp.in', bold: true },
          { text: ' and the associated mobile application titled ' },
          { text: '\u2018Zet Partner\u2019', italic: true },
          {
            text: ' along with all upgrades, updates, enhancement and modifications thereto (hereinafter referred to as \u201c',
          },
          { text: 'App', bold: true },
          {
            text: '\u201d) is owned and managed by ZET Technolabs Private Limited',
          },
          { text: ',', italic: true },
          {
            text: ' a company incorporated under the provisions of the Companies Act, 2013 and having its registered office Olsen Spaces, 2nd Floor HSR Layout Sector 6, 12th Main 14th Cross Opp JSS Public School Bengaluru 560102 (hereinafter referred to as \u201c',
          },
          { text: 'Company', bold: true },
          { text: '\u201d or \u201c' },
          { text: 'We', bold: true },
          { text: '\u201d or \u201c' },
          { text: 'Us', bold: true },
          {
            text: '\u201d which expression shall, unless it be repugnant to the context or meaning thereof, be deemed to mean and include its successors, affiliates, and permitted assigns).',
          },
        ],
      },
      {
        kind: 'paragraph',
        number: '1.4',
        runs: [
          { text: 'Please read these Terms of Use (\u201c' },
          { text: 'Terms', bold: true },
          {
            text: '\u201d) carefully, as it contains the terms and conditions governing your use of the App and your provision of the Information (defined below) that Company may request from you on or through the App and/or through associated website for any services that Company may provide through the App. Before providing any Information on the App you must first agree to these Terms. Please do not access or use the App or otherwise click to accept these Terms, if and when prompted on the App, if you do not agree with any of the terms provided below or are unable to be bound by them.',
          },
        ],
      },
      {
        kind: 'paragraph',
        number: '1.5',
        runs: [
          {
            text: 'You should read the Terms along with the linked information including but not limited to Company\u2019 privacy policy (\u201c',
          },
          { text: 'Policy', bold: true },
          {
            text: '\u201d) which is hereby incorporated by reference into these Terms.',
          },
        ],
      },
      {
        kind: 'paragraph',
        number: '1.6',
        runs: [
          {
            text: 'If you are unwilling or unable to be legally bound by these Terms, please do not use the App or provide us any Information hereto or otherwise click to accept these Terms, if and when prompted on the App. You cannot accept these Terms if: (a) you are not lawfully entitled to use the App and/or receive the services provided on the App; or (b) if you are not of legal age to form a binding agreement with Company.',
          },
        ],
      },
    ],
  },
  {
    number: '2',
    title: 'MEMBERSHIP ELIGIBILITY',
    blocks: [
      {
        kind: 'paragraph',
        runs: [
          {
            text: 'This Service is only available only to natural persons resident in India who can enter into legally binding contracts under the Indian Contract Act, 1872. Persons who are \u201cincompetent to contract\u201d within the meaning of the Indian Contract Act, 1872 including minors, un-discharged insolvents and persons of unsound mind are not eligible to access the App and/or provide any Information hereto. Any person under the age of 18 shall not register on App and shall not transact on or use the App. If a minor wishes to use or transact on the App, such use or transaction may be made by the minor\u2019s legal guardian or parents on the App. Company reserves the right to terminate any person\u2019s membership in accordance with the terms of these Terms and/or refuse to provide such person with access to the App if it is brought to the notice of Company or if Company discovers for itself that such person is not eligible to use the App.',
          },
        ],
      },
    ],
  },
  {
    number: '3',
    title: 'ACCEPTANCE OF TERMS',
    blocks: [
      {
        kind: 'paragraph',
        number: '3.1',
        runs: [
          {
            text: 'In order to use the App, you must first agree to these Terms. You can accept the Terms by:',
          },
        ],
      },
      {
        kind: 'listItem',
        marker: '(a)',
        runs: [
          {
            text: 'Accessing the App and/or provision of any Information on the App. In this case, you understand and agree that Company will treat your access to the App and/or provision of Information on the App as acceptance of the Terms from that point onwards; or',
          },
        ],
      },
      {
        kind: 'listItem',
        marker: '(b)',
        runs: [
          {
            text: 'By clicking to accept these Terms, if and when prompted on the App.',
          },
        ],
      },
    ],
  },
  {
    number: '4',
    title: 'YOUR INFORMATION',
    blocks: [
      {
        kind: 'paragraph',
        number: '4.1',
        runs: [
          {
            text: 'For the provision of Services (defined below) through the App, Company may ask you to fill in certain information. This information may include your (i) name, (ii) phone number, (iii) email address, (iv) credit score, (iv) income range (v) pin codes, (vi) copy of any official identifier, including without limitation, Aadhar card or PAN card (hereinafter, \u201c',
          },
          { text: 'Information', bold: true },
          {
            text: "\u201d). While providing Information on the App, you shall not provide Information for anyone other than yourself, unless such person's prior permission has been obtained.",
          },
        ],
      },
      {
        kind: 'paragraph',
        number: '4.2',
        runs: [
          {
            text: 'You agree that the Information provided by you to Company, at all times, will be true, accurate, up-to-date, and complete. You further agree that if you provide any Information that is untrue, inaccurate, not up-to-date or incomplete or becomes untrue, inaccurate or incomplete or if Company has reasonable grounds to suspect that such information is untrue, inaccurate, not up-to-date, incomplete or not in accordance with these Terms, Company shall have the right to indefinitely suspend or terminate or block access your access to the App and refuse to provide you access to the App. You are advised to use due caution when providing any Information to Company accordingly.',
          },
        ],
      },
    ],
  },
  {
    number: '5',
    title: 'SERVICES AND CONSENT TO USE OF INFORMATION',
    blocks: [
      {
        kind: 'paragraph',
        number: '5.1',
        runs: [
          {
            text: 'All users will need to comply with the terms of these Terms in order to be able to use and access the services on the App, which include (a) recommending you personalized Partner (defined below) products based on the Information shared by you; (b) providing you a list of Partner products you are eligible for, based on the Information provided by you; and (c) providing such other services as may be made available on the App from time to time (\u201c',
          },
          { text: 'Services', bold: true },
          {
            text: '\u201d). Company is not and will not be responsible for any claim or for any damages suffered that are related, directly or indirectly, to or arising out of your use of any Service on or through the App.',
          },
        ],
      },
      {
        kind: 'paragraph',
        number: '5.2',
        runs: [
          {
            text: 'Company partners with certain financial services providers and other Companies (\u201c',
          },
          { text: 'Partner', bold: true },
          {
            text: '\u201d) in order to recommend you financial products and services that are provided by the Partner and are best suited to you based on the Information provided by you. You understand that Company may store your Information and share your Information with the Partners and/or the analysis of such Information with the Partners to facilitate your availing of their products.',
          },
        ],
      },
      {
        kind: 'paragraph',
        number: '5.3',
        runs: [
          {
            text: 'Company is associated with several individuals who shall be interacting with you (\u201c',
          },
          { text: 'Zet Partner users', bold: true },
          {
            text: '\u201d) to provide you the recommendations of the Partner products based on your Information. You agree to receive such communication from the Zet Partner users.',
          },
        ],
      },
      {
        kind: 'paragraph',
        number: '5.4',
        runs: [
          {
            text: 'The provision of Services on the App may be interrupted, including for maintenance, repairs, upgrades, or network or equipment failures. Company strives to keep the App up and running; however, all online services suffer occasional disruptions and outages, and Company isn\u2019t liable for any disruption or loss you may suffer as a result.',
          },
        ],
      },
      {
        kind: 'paragraph',
        number: '5.5',
        runs: [
          {
            text: 'Company may discontinue some or all of the Services provided on the App, as it may deem fit, including certain features and the support for certain devices and platforms, at any time.',
          },
        ],
      },
      {
        kind: 'paragraph',
        number: '5.6',
        runs: [
          {
            text: 'You hereby appoint ZET Technolabs Private Limited, having its registered office at Olsen Spaces, 2nd Floor HSR Layout Sector 6, 12th Main 14th Cross Opp JSS Public School Bengaluru - 560102, as your lawful agent to receive your credit report/credit history (\u201c',
          },
          { text: 'Credit Information', bold: true },
          { text: '\u201d) from Credit Information Companies (\u201c' },
          { text: 'CICs', bold: true },
          { text: '\u201d) as notified by Reserve Bank of India.' },
        ],
      },
      {
        kind: 'paragraph',
        number: '5.7',
        runs: [
          {
            text: 'You further authorize Company to share with CICs, your personal information/details to procure your Credit Information on a monthly frequency and use the same to provide you with service of offering financial products suited to your needs (\u201c',
          },
          { text: 'Purpose', bold: true },
          { text: '\u201d).' },
        ],
      },
      {
        kind: 'paragraph',
        number: '5.8',
        runs: [
          {
            text: 'You understand and acknowledge that the CIC shall not be liable under law or equity, in any manner whatsoever, for providing your Credit Information to Company and you will not raise any claim or objections against the CIC. You confirm that this consent is given by your free will and not due to any solicitation by any person/entity.',
          },
        ],
      },
      {
        kind: 'paragraph',
        number: '5.9',
        runs: [
          {
            text: 'This consent/authorization shall be valid for (i) a maximum period of 06 (six) months, from the date of giving the same, (ii) satisfying the Purpose or (iii) until you withdraw your consent at any time, by informing the same to Company, at their registered office address, website, assigned email id or mobile application, whichever is earlier. You also provide consent to the terms of use of CRIF High Mark Credit Score, Experian PLC, Equifax Inc. and TransUnion CIBIL Limited, as amended/updated from time to time.',
          },
        ],
      },
    ],
  },
  {
    number: '6',
    title: 'AGREEMENT TO RECEIVE COMMUNICATION',
    blocks: [
      {
        kind: 'paragraph',
        runs: [
          {
            text: 'You hereby by way of accepting these Terms consent to the receipt of communication from Company, Zet Partner users and/or the Partners by way of in app messages, Short Message Service (SMS) messages, e-mails, promotional, marketing calls and newsletters. You agree to receive this communication irrespective of whether you have enabled \u201cdo-not-disturb\u201d on your electronic device. These emails could relate to your transactions that you carry out through the App and promotions that are undertaken by Company, Services from Company and its third-party partners, as applicable.',
          },
        ],
      },
    ],
  },
  {
    number: '7',
    title: 'LINKS TO THIRD PARTY WEBSITES',
    blocks: [
      {
        kind: 'paragraph',
        runs: [
          {
            text: 'The App may contain links and interactive functionality interacting with the websites of third parties. Company is not responsible for and has no liability for the functionality, actions, inactions, privacy settings, privacy policies, terms, or content of any such websites. Before enabling any sharing functions to communicate with any such website or otherwise visiting any such website, Company strongly recommends that you review and understand the terms and conditions, privacy policies, settings, and information-sharing functions of each such third-party website.',
          },
        ],
      },
    ],
  },
  {
    number: '8',
    title: 'USE OF THE APP',
    blocks: [
      {
        kind: 'paragraph',
        number: '8.1',
        runs: [
          {
            text: 'You agree, undertake and covenant that, during the use of the App, you shall not host, display, upload, modify, publish, transmit, store, update or share any information that:',
          },
        ],
      },
      {
        kind: 'listItem',
        marker: '(a)',
        runs: [
          {
            text: 'belongs to another person and to which you do not have any right, except as expressly provided in these Terms.',
          },
        ],
      },
      {
        kind: 'listItem',
        marker: '(b)',
        runs: [
          {
            text: "is grossly harmful, harassing, blasphemous, defamatory, obscene, pornographic, pedophilic, libelous, invasive of another person's privacy, insulting or harassing on the basis of gender, hateful or racially or ethnically objectionable, relating or encouraging money laundering or gambling or an online game that causes harm, or promoting enmity between different groups on the grounds of religion or caste with the intent to incite violence, or otherwise inconsistent or contrary to the applicable laws in force in any manner whatsoever.",
          },
        ],
      },
      {
        kind: 'listItem',
        marker: '(c)',
        runs: [{ text: 'is misleading in any way.' }],
      },
      {
        kind: 'listItem',
        marker: '(d)',
        runs: [{ text: 'is harmful to minors.' }],
      },
      {
        kind: 'listItem',
        marker: '(e)',
        runs: [
          {
            text: 'involves the transmission of "junk mail", "chain letters", or unsolicited mass mailing or "spamming".',
          },
        ],
      },
      {
        kind: 'listItem',
        marker: '(f)',
        runs: [
          {
            text: "infringes upon or violates any third party's rights including, but not limited to, any patent, trademark, copyright or other proprietary rights or intellectual property rights, rights of privacy (including without limitation unauthorized disclosure of a person's name, email address, physical address, or phone number).",
          },
        ],
      },
      {
        kind: 'listItem',
        marker: '(g)',
        runs: [
          {
            text: "provides instructional information about illegal activities such as violating someone's privacy or providing or creating computer viruses.",
          },
        ],
      },
      {
        kind: 'listItem',
        marker: '(h)',
        runs: [
          {
            text: 'tries to gain unauthorized access or exceeds the scope of authorized access to the App or to profiles, communities, Account information, bulletins, or other areas of the App or solicits passwords or personally identifying information for commercial or unlawful purposes from other users of the App.',
          },
        ],
      },
      {
        kind: 'listItem',
        marker: '(i)',
        runs: [
          {
            text: 'engages in commercial activities without Company\u2019 prior written consent such as engages in contests, sweepstakes, barter, advertising etc.',
          },
        ],
      },
      {
        kind: 'listItem',
        marker: '(j)',
        runs: [
          { text: 'interferes with another person\u2019s use of the App.' },
        ],
      },
      {
        kind: 'listItem',
        marker: '(k)',
        runs: [{ text: 'impersonates another person.' }],
      },
      {
        kind: 'listItem',
        marker: '(l)',
        runs: [
          {
            text: 'threatens the unity, integrity, defense, security or sovereignty of India, friendly relations with foreign States, or public order, or causes incitement to the commission of any cognizable offence or prevents investigation of any offence or is insulting any foreign States.',
          },
        ],
      },
      {
        kind: 'listItem',
        marker: '(m)',
        runs: [
          {
            text: 'refers to any website or URL that, in Company\u2019 sole discretion, contains material that is inappropriate for the App or any other website, contains content that would be prohibited or violates the letter or spirit of these Terms.',
          },
        ],
      },
      {
        kind: 'listItem',
        marker: '(n)',
        runs: [
          {
            text: 'deceives or misleads the addressee/users about the origin of the messages or knowingly and intentionally communicates any information which is patently false or misleading or grossly offensive or menacing in nature but may reasonably be perceived as a fact.',
          },
        ],
      },
      {
        kind: 'listItem',
        marker: '(o)',
        runs: [
          {
            text: 'is patently false and untrue, and is written or published in any form, with the intent to mislead or harass a person, entity, or agency for financial gain or to cause any injury to any person.',
          },
        ],
      },
      {
        kind: 'listItem',
        marker: '(p)',
        runs: [
          {
            text: 'contains software viruses or any other computer codes, files or programs designed to interrupt, destroy, or limit the functionality of any computer resource; or contains any trojan horses, worms or other computer programming routines that may damage, detrimentally interfere with, diminish value of, covertly intercept or steal any system, data or personal information.',
          },
        ],
      },
      {
        kind: 'listItem',
        marker: '(q)',
        runs: [
          {
            text: 'is in the nature of an online game that is not verified as a permissible online game.',
          },
        ],
      },
      {
        kind: 'listItem',
        marker: '(r)',
        runs: [
          {
            text: 'is in the nature of advertisement or surrogate advertisement or promotion of an online game that is not a permissible online game, or of any online gaming intermediary offering such an online game.',
          },
        ],
      },
      {
        kind: 'listItem',
        marker: '(s)',
        runs: [
          { text: 'violates any applicable law for the time being in force.' },
        ],
      },
      {
        kind: 'paragraph',
        number: '8.2',
        runs: [
          {
            text: 'You hereby accept full responsibility for any consequences that may arise from your use of the App, and expressly agree and acknowledge than in no event and under no circumstances shall Company be held liable to you for any liabilities or damages resulting from or arising out of your use of the App.',
          },
        ],
      },
      {
        kind: 'paragraph',
        number: '8.3',
        runs: [
          {
            text: 'You agree and acknowledge that you shall not use the App for any fraudulent, malicious, illegal, or unauthorized purpose/activities. You agree to comply with all applicable laws pertaining to your use of the App.',
          },
        ],
      },
      {
        kind: 'paragraph',
        number: '8.4',
        runs: [
          {
            text: 'Company shall have the right, but not the obligation, to monitor your access to or use of the App to ensure your compliance with these Terms or applicable laws, at its sole discretion.',
          },
        ],
      },
    ],
  },
  {
    number: '9',
    title: 'TERMINATION OF ACCESS TO APP OR DEACTIVATION OF SERVICE',
    blocks: [
      {
        kind: 'paragraph',
        runs: [
          {
            text: 'Your access to the App and/or use of the Services and/or the products provided by the Partner based on the Information can be terminated at any time by Company, in its sole discretion for any reason or no reason, including your violation of these Terms. You acknowledge that such termination may be affected without any prior notice. The disclaimer of warranties, the limitation of liability, and the governing law provisions shall survive such termination.',
          },
        ],
      },
    ],
  },
  {
    number: '10',
    title: 'REFERRALS AND REWARDS',
    blocks: [
      {
        kind: 'paragraph',
        runs: [
          {
            text: 'From time to time, Company may offer referral bonuses and rewards to you for inviting new users to the App and for completing other activities.',
          },
        ],
      },
    ],
  },
  {
    number: '11',
    title: 'RIGHTS & RESERVATIONS',
    blocks: [
      {
        kind: 'paragraph',
        runs: [
          {
            text: 'Company reserves the right to deny access to the App or any portion thereof without notice for the following reasons (i) any unauthorized access or use by you; (ii) attempt(s) to assign or transfer any rights granted to you under these Terms; and (iii) violation of any of the provisions of the Terms or any applicable laws.',
          },
        ],
      },
    ],
  },
  {
    number: '12',
    title: 'DISCLAIMER OF WARRANTIES',
    blocks: [
      {
        kind: 'paragraph',
        runs: [
          {
            text: 'THE APP AND ALL INFORMATION, CONTENT, MATERIALS AND SERVICES INCLUDED ON OR OTHERWISE MADE AVAILABLE TO YOU THROUGH THE APP AND/OR THROUGH THE ZET PARTNER USERS (COLLECTIVELY, THE "',
          },
          { text: 'CONTENT', bold: true },
          {
            text: '") ARE PROVIDED BY COMPANY ON AN "AS IS," "AS AVAILABLE" BASIS, WITHOUT REPRESENTATIONS OR WARRANTIES OF ANY KIND. COMPANY MAKES NO REPRESENTATIONS OR WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, AS TO THE OPERATION OF THE APP/APP, THE ACCURACY OR COMPLETENESS OF THE CONTENT OR THE SERVICES AND THE ACCURACY OF THE INFORMATION. WITHOUT LIMITING THE FOREGOING, COMPANY MAKES NO WARRANTY THAT THE APP OR SERVICES PROVIDED BY COMPANY OR THE PARTNERS WILL MEET YOUR REQUIREMENTS OR YOUR USE OF THE APP WILL BE UNINTERRUPTED, TIMELY, SECURE OR ERROR-FREE OR THE RESULTS THAT MAY BE OBTAINED FROM THE USE OF THE APP WILL BE EFFECTIVE, ACCURATE OR RELIABLE.',
          },
        ],
      },
      {
        kind: 'paragraph',
        runs: [
          {
            text: 'YOU ACKNOWLEDGE AND UNDERSTAND THAT THIRD PARTY SERVICES ARE BEING MADE AVAILABLE ON THE APP IN RELATION TO WHICH COMPANY MAY HAVE PARTNERSHIPS WITH SOME OF THESE THIRD PARTIES INCLUDING WIHTOUT LIMITATION PARTNERS. HOWEVER, YOU ACKNOWLEDGE AND AGREE THAT COMPANY, AT NO TIME, IS MAKING ANY REPRESENTATIONS OR WARRANTIES REGARDING PROVISION OF SERVICES BY ANY OF THESE THIRD PARTIES NOR WILL COMPANY BE LIABLE TO YOU OR ANY THIRD PARTY FOR ANY CONSEQUENCES OR CLAIMS ARISING OUT OF OR IN CONNECTION WITH SUCH THIRD PARTIES.',
          },
        ],
      },
      {
        kind: 'paragraph',
        runs: [
          {
            text: 'COMPANY SHALL HAVE NO RESPONSIBILITY FOR ANY DAMAGE TO YOUR MOBILE PHONE OR LOSS OF DATA THAT RESULTS FROM THE DOWNLOAD OF ANY CONTENT, MATERIALS, DOCUMENTS, OR INFORMATION. YOU EXPRESSLY AGREE THAT THE USE OF APP IS AT YOUR OWN DISCRETION AND SOLE RISK AND THAT YOU WILL BE SOLELY RESPONSIBLE FOR ANY DAMAGE RESULTING FROM ANY TRANSACTION. NO ADVICE OR INFORMATION, WHETHER ORAL OR WRITTEN, OBTAINED BY YOU FROM COMPANY OR THROUGH OR FROM THE APP OR CONTENT SHALL CREATE ANY WARRANTY. YOU WILL NOT SOLELY RELY ON THE VIEWS, REPRESENTATIONS (WHETHER WRITTEN OR ORAL OR OTHERWISE), RECOMMENDATIONS, OPINIONS, REPORTS, ANALYSIS, INFORMATION OR OTHER STATEMENT MADE BY COMPANY OR ANY OF ITS AGENTS, DIRECTORS, OFFICERS, OR EMPLOYEES.',
          },
        ],
      },
      {
        kind: 'paragraph',
        runs: [
          {
            text: 'IN THE EVENT THAT YOU SEEK TO MAKE PURCHASE ANY PRODUCTS OR SERVICE SOLD BY THE PARTNER PURSUANT TO THE INFORMATION OR RECOMMENDATION PROVIDED BY COMPANY, YOU SHALL DO SO AT YOUR OWN RISK, AND SHOULD CONSIDER YOUR FINANCIAL SITUATION, OBJECTIVES AND NEEDS AND CONSULT WITH YOUR LEGAL, BUSINESS, TAX AND/OR OTHER PROFESSIONAL ADVISORS TO DETERMINE THE APPROPRIATENESS AND CONSEQUENCES OF ANY SUCH PURCHASE. YOU AGREE THAT ALL THE FINAL DECISIONS WILL BE YOURS AND COMPANY WILL NOT HAVE ANY LIABILITY FOR CONSEQUENCES OF SUCH DECISIONS.',
          },
        ],
      },
    ],
  },
  {
    number: '13',
    title: 'LIMITATION OF LIABILITY',
    blocks: [
      {
        kind: 'paragraph',
        runs: [
          {
            text: 'In no event shall Company, its affiliates and each of their respective officers, directors, employees and agents (as applicable) be liable to you for any special, incidental, indirect, consequential, exemplary or punitive damages, or loss of profit or revenues whatsoever resulting from any (a) errors, mistakes, or inaccuracies of Content; (b) any unauthorized access to or use of the secure servers and/or any and all personal and/or other user information stored therein; (c) any interruption or cessation of transmission to or from the Platform; (d) any bugs, viruses, trojan horses, or the like, which may be transmitted to or through the Platform by any third party; (e) any loss of your data arising from any use of or inability to use any Content or other parts of the Platform; (g) non-availability of the Platform in certain geographical areas; and/or (h) any errors or omissions in any content or for any loss or damage of any kind incurred as a result of the use of any content posted, emailed, transmitted, or otherwise made available via. the Platform, whether based on warranty, contract, tort, or any other legal theory, and whether or not Company is advised of the possibility of such damages.',
          },
        ],
      },
    ],
  },
  {
    number: '14',
    title: 'INDEMNIFICATION',
    blocks: [
      {
        kind: 'paragraph',
        runs: [
          {
            text: "You shall indemnify and hold harmless Company, its affiliates and their respective officers, directors, agents, and employees, from any and all claims or demands, or actions including reasonable attorneys' fees, made by any third party or penalty imposed due to or arising out of your breach of these Terms, Policy and other policies, or your violation of any law, rules or regulations or the rights (including infringement of any intellectual property rights) of a third party.",
          },
        ],
      },
    ],
  },
  {
    number: '15',
    title: 'PRIVACY POLICY',
    blocks: [
      {
        kind: 'paragraph',
        runs: [
          {
            text: 'Please review the Policy, which also governs your visit to the App, to understand Company\u2019 privacy practices. The personal information/data provided to Company by you during the course of usage of the App will be treated as strictly confidential and in accordance with the Policy and applicable laws and regulations. If you object to your information being transferred or used, please do not use the App.',
          },
        ],
      },
    ],
  },
  {
    number: '16',
    title: 'ASSIGNMENT',
    blocks: [
      {
        kind: 'paragraph',
        runs: [
          {
            text: 'You shall not license, sell, transfer, or assign your rights, obligations, or covenants under these Terms in any manner without Company\u2019 prior written consent. Company reserves the right, at its own discretion, to freely assign and transfer the rights and obligations under these Terms to any third party.',
          },
        ],
      },
    ],
  },
  {
    number: '17',
    title: 'GOVERNING LAW AND DISPUTE RESOLUTION',
    blocks: [
      {
        kind: 'paragraph',
        runs: [
          {
            text: 'These Terms shall be governed by and interpreted and construed in accordance with the laws of India. The place of jurisdiction shall exclusively be in Bangalore, Karnataka. In the event of any dispute arising out of these Terms the same shall be settled by a binding arbitration conducted by a sole arbitrator, appointed jointly by both parties and governed by the Arbitration and Conciliation Act, 1996. The venue and seat of arbitration shall be Bangalore, Karnataka.',
          },
        ],
      },
    ],
  },
  {
    number: '18',
    title: 'SEVERABILITY AND WAIVER',
    blocks: [
      {
        kind: 'paragraph',
        runs: [
          {
            text: 'These Terms, the Policy and other referenced material herein or on the App, are the entire agreement between you and Company with respect to the products/Services offered on the App and supersede all prior or contemporaneous communications and proposals (whether oral, written, or electronic) between you and Company with respect thereto and govern your future relationship with Company. If any provision of the Terms is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary so that the Terms will otherwise remain in full force and effect and enforceable. The failure of either party to exercise in any respect any right provided for herein shall not be deemed a waiver of any further rights hereunder.',
          },
        ],
      },
    ],
  },
  {
    number: '19',
    title: 'GRIEVANCE REDRESSAL MECHANISM',
    blocks: [
      {
        kind: 'paragraph',
        number: '19.1',
        runs: [
          {
            text: 'In accordance with the Information Technology Act, 2000 and Rules made thereunder, the name and contact details of the Grievance Officer are provided below:',
          },
        ],
      },
      {
        kind: 'paragraph',
        runs: [{ text: 'Name: Mohan Singhal' }],
      },
      {
        kind: 'paragraph',
        runs: [{ text: 'Email: hi@zetapp.in' }],
      },
      {
        kind: 'paragraph',
        runs: [{ text: 'Phone No: 7417274072' }],
      },
      {
        kind: 'paragraph',
        number: '19.2',
        runs: [
          {
            text: 'For registering your complaint, please write to the Grievance Officer at the above-mentioned email address in relation to any violation of these Terms or the Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021. The Grievance Officer shall redress the complaint in accordance with the provisions of the Information Technology Act, 2000 and Rules made thereunder.',
          },
        ],
      },
    ],
  },
  {
    number: '20',
    title: 'HOW TO CONTACT US',
    blocks: [
      {
        kind: 'paragraph',
        runs: [
          {
            text: 'If you have questions or concerns about these Terms, please contact Company at the following email address : hi@zetapp.in',
          },
        ],
      },
    ],
  },
  {
    number: '21',
    title: 'LIST OF OUR PARTNERS',
    blocks: [
      {
        kind: 'paragraph',
        runs: [
          {
            text: 'Please refer to the list of our Partners at Our Partners | ZET',
          },
        ],
      },
    ],
  },
]
